import React, { useContext, useEffect, useState } from "react";
import { Button, Typography } from "@mui/material";
import { getDigits } from "@fac/utils";
import { formatPhone, isCNPJValid } from "@fac/props-helpers";
import { BeatLoader } from "react-spinners";
import { FacTextFieldCNPJ, LoadingContainer, MultiStepContextLayout } from "@fac/standard-ui-lib";
import { isAxiosError } from "axios";
import { debounce } from "lodash"; // Import debounce function
import CompanyStepRoot from "./CompanyStep.styles";
import { useGetGOVCompany } from "../../modules/GOV";
import ClientContextLayout from "../../modules/Client/Client.context";

export const CompanyStep = () => {
    const { nextStep, step } = useContext(MultiStepContextLayout);
    const { setClient } = useContext(ClientContextLayout);
    const [CNPJ, setCNPJ] = useState<string | undefined>();
    const { company, loaded, getCompany, setCompany, error } = useGetGOVCompany();
    const [validCompany, setValidCompany] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string | undefined>();

    useEffect(() => {
        if (step === 1) {
            setCompany(undefined);
            setClient(undefined);
            setCNPJ("");
        }
    }, [step, setCompany, setClient]);

    useEffect(() => {
        setErrorMsg(undefined);
        if (error && isAxiosError(error)) {
            if (error.response?.status === 404) {
                setErrorMsg("O CNPJ informado não existe.");
            }
        }
    }, [error]);

    // Debounced API call to prevent excessive requests
    const debouncedGetCompany = debounce((cnpj) => {
        getCompany(getDigits(cnpj));
    }, 500);

    const onClientConfirm = () => {
        if (!CNPJ || !company) return;
        const phone = (
            (company?.telefones?.[0].ddd || "") + (company?.telefones?.[0].numero || "")
        ).toString();
        setClient({
            cnpj: getDigits(CNPJ),
            email: company?.correioEletronico,
            phone: formatPhone(
                phone.length === 10 ? `${phone.substring(0, 2)}9${phone.substring(2)}` : phone,
            ),
            govData: company,
        });
        nextStep();
    };

    const onChangeCNPJ: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
        if (e?.target?.value?.length === 18) {
            setErrorMsg(undefined);
            setCompany(undefined);
        }
        if (isCNPJValid(e.target.value)) {
            debouncedGetCompany(e.target.value); // Use debounced function
        } else {
            setErrorMsg("O CNPJ inserido está inválido");
        }
        setCNPJ(e.target.value);
    };

    useEffect(() => {
        if (!company) {
            setValidCompany(false);
            return;
        }
        if (company.informacoesAdicionais?.optanteMei !== "SIM") {
            setErrorMsg("A empresa inserida não é optante MEI");
            setValidCompany(false);
            return;
        }
        setValidCompany(true);
    }, [company]);

    return (
        <CompanyStepRoot>
            <form
                onSubmit={(e) => {
                    e?.preventDefault();
                    onClientConfirm();
                }}
            >
                <header>
                    <Typography variant="h5" component="h1" color="white">
                        Insira seu CNPJ para iniciar o atendimento
                    </Typography>
                </header>
                <FacTextFieldCNPJ
                    name="company-cnpj"
                    value={CNPJ}
                    onChange={onChangeCNPJ}
                    label=""
                    hiddenLabel
                    variant="filled"
                    subVariant="clean-transparent"
                />
            </form>
            <footer>
                {!loaded && !company && (
                    <LoadingContainer fullWidth>
                        <BeatLoader size={15} color="white" speedMultiplier={0.5} />
                    </LoadingContainer>
                )}
                {loaded && errorMsg && (
                    <Typography variant="h3" color="error" fontWeight={400}>
                        {errorMsg}
                    </Typography>
                )}
                {loaded && company && validCompany && (
                    <>
                        <Typography className="company-name" variant="h3" color="white">
                            A sua empresa é <b>{company?.nomeEmpresarial}</b>?
                        </Typography>
                        <Button type="submit" variant="clean" onClick={onClientConfirm}>
                            Confirmar
                        </Button>
                    </>
                )}
            </footer>
        </CompanyStepRoot>
    );
};

export default CompanyStep;
